<template>
  <div class="flex-1 flex flex-col p-4">
    <!-- Header -->
    <div class="mb-4">
      <h2 class="text-base font-semibold text-gray-800">Dictionary Types</h2>
      <p class="text-sm text-gray-500 mt-1">Create and manage dictionary collections used for name search
        classification.</p>
    </div>

    <!-- Add Dictionary Modal -->
    <AppModal v-model="showCreate" title="Add Dictionary" hide-footer>
      <div class="p-4">
        <form @submit.prevent="submit" class="space-y-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Dictionary Name</label>
            <input
              type="text"
              v-model="form.name"
              placeholder="Dictionary name"
              required
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
            />
          </div>
          <div class="border-t border-gray-100 pt-2 flex justify-end">
            <button type="submit" class="btn-primary" :disabled="spinner">
              <i class="fa fa-save mr-1"></i>
              <span v-if="!spinner">Save</span>
              <span v-else>Saving...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>

    <!-- Action Bar -->
    <div class="flex justify-end mb-4">
      <button class="btn-primary" @click="showCreate = true">
        <i class="fa fa-plus"></i>
        <span>Add Dictionary</span>
      </button>
    </div>

    <!-- Table -->
    <div class="bg-white rounded border border-gray-200 overflow-hidden">
      <table class="min-w-full leading-normal">
        <thead>
        <tr class="border-b border-t border-gray-200">
          <th class="t-header">#</th>
          <th class="t-header">Name</th>
          <th class="t-header">Number of Words</th>
          <th class="t-header">Action</th>
        </tr>
        </thead>
        <tbody>
        <tr :class="[(idx%2) ? rows.even : rows.odd]" v-for="(item, idx) in dictionaries" :key="idx">
          <td class="t-body">{{ idx + 1 }}</td>
          <td class="t-body">{{ item.name }}</td>
          <td class="t-body">{{ item.words }}</td>
          <td class="t-body">
            <button @click="deleteDictionary(item)" class="btn-sm-danger">
              <i class="fa fa-trash mr-1"></i> Remove
            </button>
          </td>
        </tr>
        <tr v-if="dictionaries.length === 0">
          <td colspan="4" class="text-center py-6 text-sm text-gray-400">No dictionaries found</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { useConfigurationsStore } from "../../stores";
import AppModal from "@/components/ui/AppModal.vue";

interface DictionaryForm {
  name: string | null;
}

interface Dictionary {
  id: string;
  name: string;
  words: number;
}

interface GlobalProperties {
  successToast?: (title: string, message: string) => void;
  errorToast?: (title: string, message: string) => void;
}

const store = useConfigurationsStore();
const instance = getCurrentInstance();
const proxy = instance?.proxy as unknown as GlobalProperties | undefined;

const form = ref<DictionaryForm>({ name: null });
const spinner = ref(false);
const showCreate = ref(false);
const dictionaries = ref<Dictionary[]>([]);

const rows = ref({
  odd: "border-t border-gray-200 hover:bg-gray-10",
  even: "border-t border-gray-200 bg-gray-10"
});

const dictionaryAdd = computed(() => store.dictionaryAdded);
const dictionaryFetch = computed(() => store.dictionaries);
const dictionaryAddError = computed(() => store.reservationAddError);

const submit = () => {
  spinner.value = true;
  store.addDictionary(form.value);
};

const deleteDictionary = (dictionary: Dictionary) => {
  console.log(dictionary);
};

const clearForm = () => {
  spinner.value = false;
  form.value = { name: null };
  showCreate.value = false;
};

onMounted(() => {
  store.fetchDictionary();
});

watch(dictionaryFetch, (list) => {
  if (list) {
    dictionaries.value = list;
  }
});

watch(dictionaryAdd, () => {
  proxy?.successToast?.("Success", "Dictionary added");
  store.fetchDictionary();
  clearForm();
});

watch(dictionaryAddError, (response: any) => {
  spinner.value = false;
  proxy?.errorToast?.("Error", response?.message || "Failed to add");
});
</script>
